import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { collection, onSnapshot, doc, getDocs, getDoc, setDoc, deleteDoc, updateDoc } from '../firebase.config';
import { Link } from 'react-router-dom';
import { getCompanyInfo, getCompanyTypeLabel } from '../utils/companyUtils';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { getCollectionForCompany } from '../utils/dataAdapter';
import { fetchUserSettings, fetchCompanyDetails } from '../utils/dataAdapter';
import { auth } from '../firebase.config';
import { useLanguage } from '../contexts/LanguageContext';
import { roundTo2Decimals } from '../utils/numberFormat';
import { useCompanyCurrency } from '../hooks/useCompanyCurrency';
import { formatCurrency } from '../utils/currencyUtils';
import TodoFloatingButton from './TodoFloatingButton';
// 🔒 PHASE 2: Import secure cache manager for performance
import { secureCacheManager } from '../utils/secureCacheManager';
import LoadingScreen from './LoadingScreen';
import { useComponentLoading } from '../contexts/LoadingContext';
import AppSelect from './shared/AppSelect';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const DASHBOARD_TODO_CATEGORIES_EMOJI = [
  { value: 'personal', label: '👤 Personal' },
  { value: 'meeting', label: '🤝 Meeting' },
  { value: 'urgent', label: '🚨 Urgent' },
  { value: 'followup', label: '📞 Follow Up' },
  { value: 'business', label: '💼 Business' },
  { value: 'reminder', label: '🔔 Reminder' },
  { value: 'project', label: '📊 Project' },
  { value: 'other', label: '📝 Other' },
];

const DASHBOARD_TODO_CATEGORIES_PLAIN = DASHBOARD_TODO_CATEGORIES_EMOJI.map(({ value, label }) => ({
  value,
  label: label.replace(/^[^\s]+\s/, ''),
}));

const DASHBOARD_TODO_PRIORITIES_EMOJI = [
  { value: 'low', label: '🟢 Low' },
  { value: 'medium', label: '🟡 Medium' },
  { value: 'high', label: '🔴 High' },
];

const DASHBOARD_TODO_PRIORITIES_PLAIN = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const DASHBOARD_TODO_FILTER_CATEGORIES = [
  { value: 'all', label: '📋 All Categories' },
  ...DASHBOARD_TODO_CATEGORIES_EMOJI,
];

const dashboardTodoSelectTrigger =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 text-left min-h-[44px] flex items-center justify-between touch-manipulation';

const dashboardTodoSelectTriggerLg =
  'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left min-h-[44px] flex items-center justify-between touch-manipulation';

const dashboardFilterSelectTrigger =
  'w-full p-3 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white transition-all duration-200 hover:border-gray-300 text-left min-h-[44px] flex items-center justify-between touch-manipulation';

// Calculate loan interest based on interest rate and time elapsed
// interestFrequency: 'yearly' or 'monthly'
const calculateLoanInterest = (principal, interestRate, loanDate, payments = [], interestFrequency = 'yearly') => {
  if (!principal || !interestRate || !loanDate) return 0;
  
  try {
    const loanStartDate = new Date(loanDate);
    const currentDate = new Date();
    
    // Calculate days elapsed since loan start
    const daysElapsed = Math.max(0, Math.floor((currentDate - loanStartDate) / (1000 * 60 * 60 * 24)));
    
    // Calculate total paid from payments
    const totalPaid = payments.reduce((sum, payment) => {
      const paymentDate = new Date(payment.paymentDate || payment.createdAt?.toDate?.() || currentDate);
      // Only count payments made before or on current date
      if (paymentDate <= currentDate) {
        return sum + (parseFloat(payment.amount) || 0);
      }
      return sum;
    }, 0);
    
    // Calculate remaining principal after payments
    const remainingPrincipal = Math.max(0, principal - totalPaid);
    
    // Calculate interest based on frequency
    const interestRateDecimal = interestRate / 100;
    let interest = 0;
    
    if (interestFrequency === 'monthly') {
      // Monthly interest calculation
      // Calculate months elapsed (more accurate than days/30)
      const monthsElapsed = Math.max(0, (currentDate.getFullYear() - loanStartDate.getFullYear()) * 12 + 
        (currentDate.getMonth() - loanStartDate.getMonth()) + 
        (currentDate.getDate() >= loanStartDate.getDate() ? 0 : -1));
      
      // Monthly rate = annual rate / 12
      const monthlyRate = interestRateDecimal / 12;
      // Interest = Principal × Monthly Rate × Months
      interest = remainingPrincipal * monthlyRate * monthsElapsed;
    } else {
      // Yearly interest calculation (default)
      // Interest = Principal × Rate × Time (in years)
      const timeInYears = daysElapsed / 365;
      interest = remainingPrincipal * interestRateDecimal * timeInYears;
    }
    
    return Math.max(0, interest);
  } catch (error) {
    console.error('Error calculating loan interest:', error);
    return 0;
  }
};

const Dashboard = ({ db, userId, isAuthReady, appId }) => {
    const { currentCompany } = useEnhancedCompany();
    const companyId = currentCompany?.companyId || currentCompany?.id || null;
    const { currency: companyCurrency } = useCompanyCurrency();
    const { t, currentLanguage, changeLanguage } = useLanguage();
    
    // Mobile view: native app or narrow viewport
    const [isMobileView, setIsMobileView] = useState(false);
    
    // Set initial loading state immediately when component mounts
    const [isInitialMount, setIsInitialMount] = useState(true);
    
    useEffect(() => {
        const checkMobileView = () => {
            const isCapacitor = typeof window !== 'undefined' && window.Capacitor;
            const isNarrow = typeof window !== 'undefined' && window.innerWidth < 768;
            setIsMobileView(isCapacitor || isNarrow);
        };
        
        checkMobileView();
        window.addEventListener('resize', checkMobileView);
        return () => window.removeEventListener('resize', checkMobileView);
    }, []);
    
    // Language change handled silently
    const [totalCustomers, setTotalCustomers] = useState(0);
    const [totalSuppliers, setTotalSuppliers] = useState(0);
    const [totalItems, setTotalItems] = useState(0);
    const [totalSalesThisMonth, setTotalSalesThisMonth] = useState(0);
    const [totalPurchasesThisMonth, setTotalPurchasesThisMonth] = useState(0);
    const [lastSevenDaysSales, setLastSevenDaysSales] = useState(0);
    const [totalLoansOutstanding, setTotalLoansOutstanding] = useState(0);
    const [companyDetails, setCompanyDetails] = useState({ firmName: '', gstin: '', contactNumber: '', address: '', gstinType: '' });
    const [companyInfo, setCompanyInfo] = useState(null);
    const [userInfo, setUserInfo] = useState(null);
    const [message, setMessage] = useState('');
    const [payments, setPayments] = useState([]);
    
    // Loading and error states - START WITH LOADING TRUE
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    
    // Initial loading state - set to TRUE immediately on mount, before any data operations
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    
    // Track if we've shown loading screen at least once (minimum display time)
    const [hasShownLoading, setHasShownLoading] = useState(false);
    const [loadingStartTime, setLoadingStartTime] = useState(null);
    // Native: no artificial min pad; web keeps brief loader pad
    const isNativeApp = Capacitor.isNativePlatform();
    const MIN_LOADING_TIME = isNativeApp ? 0 : 500;
    
    // Use loading context for better UX - MUST be defined before checkAndStopInitialLoading
    const { isLoading: isContextLoading, startLoading, stopLoading } = useComponentLoading('dashboard', 'Loading dashboard data...');
    
    // Track which data sources have loaded
    const dataLoadedRef = useRef({
        parties: false,
        payments: false,
        items: false
    });
    
    // Function to check if required data is loaded and stop initial loading
    // Native: parties + items enough for first paint (payments fill in after)
    const checkAndStopInitialLoading = useCallback(() => {
        const { parties, payments, items } = dataLoadedRef.current;
        const ready = isNativeApp
            ? (parties && items)
            : (parties && payments && items);
        if (ready) {
            const elapsed = loadingStartTime ? Date.now() - loadingStartTime : 0;
            const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsed);
            
            if (remainingTime > 0) {
                console.log(`🔄 [LOADING SCREEN] Dashboard: All data loaded, waiting ${remainingTime}ms more for minimum display time`);
                setTimeout(() => {
                    console.log('🔄 [LOADING SCREEN] Dashboard: Stopping initial loading - all data loaded and minimum time elapsed');
                    setIsInitialLoading(false);
                    setIsLoading(false);
                    stopLoading();
                }, remainingTime);
            } else {
                console.log('🔄 [LOADING SCREEN] Dashboard: Stopping initial loading - all data loaded');
                setIsInitialLoading(false);
                setIsLoading(false);
                stopLoading();
            }
        }
    }, [loadingStartTime, stopLoading, isNativeApp, MIN_LOADING_TIME]);
    
    // Start loading IMMEDIATELY on mount and track start time - BEFORE any data operations
    useEffect(() => {
        console.log('🔄 [LOADING SCREEN] Dashboard: Component mounted - starting loading IMMEDIATELY (before any data operations)');
        const startTime = Date.now();
        setLoadingStartTime(startTime);
        setIsInitialLoading(true);
        setIsLoading(true);
        startLoading('Loading dashboard data...', 'Fetching your business data from Firebase');
    }, [startLoading]);
    
    
    


                // Todo list state - Start with empty list for new users
            const [todos, setTodos] = useState([]);

    const [newTodoText, setNewTodoText] = useState('');
    const [newTodoPriority, setNewTodoPriority] = useState('medium');
    const [newTodoCategory, setNewTodoCategory] = useState('personal');
    const [showAddTodo, setShowAddTodo] = useState(false);
    const [filterCategory, setFilterCategory] = useState('all');

    const [partiesList, setPartiesList] = useState([]);
    const [partyOutstanding, setPartyOutstanding] = useState([]);

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];


                // Load todos from Firebase
            useEffect(() => {
                if (!db || !userId || !isAuthReady || !companyId) return;
                
                const loadTodos = async () => {
                    try {
                        console.log('🔄 Loading todos for company:', companyId, 'userId:', userId);
                        const todosSnap = await getDocs(getCollectionForCompany(db, { companyId, appId, userId }, 'todolist'));
                        const loadedTodos = [];
                        todosSnap.forEach(doc => {
                            loadedTodos.push({ id: parseInt(doc.id), ...doc.data() });
                        });
                        
                        console.log('✅ Todos loaded successfully:', loadedTodos.length, 'todos');
                        // Set todos from Firebase or empty array if no todos exist
                        setTodos(loadedTodos);
                    } catch (error) {
                        console.error('❌ Error loading todos:', error);
                        console.error('❌ CompanyId:', companyId, 'UserId:', userId, 'AppId:', appId);
                        // Set empty todos array on error
                        setTodos([]);
                    }
                };
                
                loadTodos();
                
                // Cleanup function
                return () => {
                    // Clear todos when component unmounts or user changes
                    setTodos([]);
                };
            }, [db, userId, isAuthReady, appId, companyId]);

    // Set loading state IMMEDIATELY when component mounts or dependencies become ready
    useEffect(() => {
        if (isInitialMount && db && userId && isAuthReady) {
            console.log('🔄 [LOADING SCREEN] Dashboard: Setting loading state IMMEDIATELY - before any Firebase operations');
            setIsLoading(true);
            startLoading('Loading dashboard data...');
            setIsInitialMount(false);
        }
    }, [isInitialMount, db, userId, isAuthReady, startLoading]);

    useEffect(() => {
        if (!db || !userId || !isAuthReady) return;
        
        // Wait for company context to be available before fetching data
        // BUT: If user has no companies, stop loading and let wizard show
        if (!companyId) {
            console.log('⏳ Dashboard: Waiting for company context to initialize...');
            
            // Check if user companies have been loaded (even if empty)
            // EnhancedCompanyContext will set currentCompany to null if user has no companies
            // Give it time to complete loading, then stop loading to show wizard
            const checkUserCompaniesLoaded = () => {
              // Wait for EnhancedCompanyContext to finish loading
              // If after a reasonable time (2 seconds) there's still no companyId,
              // assume user has no companies and stop loading to show wizard
              const timeoutId = setTimeout(() => {
                console.log('⏳ Dashboard: No companyId after timeout, user has no companies - stopping loading to show wizard');
                setIsLoading(false);
                setIsInitialLoading(false);
                stopLoading();
                setMessage('No company found. Please create your first company.');
              }, 2000);
              
              return () => clearTimeout(timeoutId);
            };
            
            setIsLoading(true);
            setMessage('Initializing company data...');
            return checkUserCompaniesLoaded();
        }
        
        // Reset data tracking when companyId changes
        dataLoadedRef.current = {
            parties: false,
            payments: false,
            items: false
        };
        
        console.log('✅ Dashboard: Company context ready, loading data for company:', companyId);
        
        // Declare unsubscribe functions outside the async function
        let unsubscribeParties = null;
        let unsubscribePayments = null;
        let unsubscribeItems = null;

        const fetchDashboardData = async () => {
            try {
                console.log('🔄 [LOADING SCREEN] Dashboard: fetchDashboardData started - loading should already be set');
                // Loading state should already be set by the initial mount effect
                setHasError(false);
                setErrorMessage('');
                setMessage('Loading dashboard data...');

                
                // 🔒 PHASE 2: Load user settings with caching for fast initial load
                try {
                    const res = await secureCacheManager.get(
                        `user_display_${userId}`,
                        async () => {
                            const response = await fetchUserSettings(db, { companyId, appId, userId });
                            if (response?.success && response?.data) {
                                // Return only non-sensitive display data
                                return {
                                    success: true,
                                    data: {
                                        displayName: response.data.displayName,
                                        email: response.data.email,
                                        companyId: response.data.companyId,
                                        // DO NOT cache tokens or sensitive IDs
                                    }
                                };
                            }
                            return response;
                        },
                        5 * 60 * 1000 // 5 minutes cache
                    );
                    
                    if (res?.success && res?.data) {
                        const userData = res.data;
                        setUserInfo(userData);
                        
                        // Company id: prefer settings doc, else context (settings may omit companyId)
                        const settingsCompanyId = userData.companyId || companyId;
                        if (settingsCompanyId) {
                            secureCacheManager.get(
                                `company_profile_v2_${settingsCompanyId}`,
                                () => getCompanyInfo(settingsCompanyId, appId),
                                10 * 60 * 1000 // 10 minutes cache
                            ).then(companyData => {
                                setCompanyInfo(companyData);
                            }).catch(() => {});

                            try {
                                const cRes = await fetchCompanyDetails(db, {
                                    companyId: settingsCompanyId,
                                    appId,
                                    userId
                                });
                                if (cRes?.success && cRes?.data) {
                                    setCompanyDetails(prev => ({ ...prev, ...cRes.data }));
                                }
                            } catch (_) {}
                        }
                    }
                } catch (error) {
                    console.error('❌ Error loading user/company info via adapter:', error);
                    // Don't fail the entire dashboard load for user info errors
                }

                // Initialize variables outside try blocks to ensure proper scope
                let parties = [];
                let paymentsData = [];

                // 🔒 PHASE 2: Hybrid approach - Fast initial load from cache + Real-time updates
                const partiesRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
                
                // Step 1: Load from cache immediately (FAST!)
                secureCacheManager.get(
                    `parties_list_${companyId}`,
                    async () => {
                        const snapshot = await getDocs(partiesRef);
                        return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    },
                    5 * 60 * 1000 // 5 minutes cache
                ).then(cachedParties => {
                    // Use cached data immediately for fast UI
                    let buyersCount = 0;
                    let sellersCount = 0;
                    cachedParties.forEach(party => {
                        if (party.partyType === 'Buyer' || party.partyType === 'Both') buyersCount++;
                        if (party.partyType === 'Seller' || party.partyType === 'Both') sellersCount++;
                    });
                    setPartiesList(cachedParties);
                    setTotalCustomers(buyersCount);
                    setTotalSuppliers(sellersCount);
                    console.log('✅ PHASE 2: Loaded parties from cache (fast!)');
                }).catch(err => {
                    console.error('Error loading cached parties:', err);
                });
                
                // Step 2: Set up real-time listener for fresh updates
                unsubscribeParties = onSnapshot(partiesRef, (snapshot) => {
                    try {
                        console.log('🔄 [LOADING SCREEN] Dashboard: Parties snapshot received', {
                            hasData: !snapshot.empty,
                            count: snapshot.size,
                            isLoading
                        });
                        let parties = [];
                        let buyersCount = 0;
                        let sellersCount = 0;
                        snapshot.forEach(doc => {
                            const partyData = doc.data();
                            parties.push({ id: doc.id, ...partyData });
                            if (partyData.partyType === 'Buyer' || partyData.partyType === 'Both') buyersCount++;
                            if (partyData.partyType === 'Seller' || partyData.partyType === 'Both') sellersCount++;
                        });
                        setPartiesList(parties);
                        setTotalCustomers(buyersCount);
                        setTotalSuppliers(sellersCount);
                        
                        // Update cache when real-time update occurs
                        secureCacheManager.set(`parties_list_${companyId}`, parties, 5 * 60 * 1000);
                        console.log('✅ Parties data updated in real-time & cache updated');
                        
                        console.log('✅ Parties loaded, triggering outstanding calculation');
                        
                        // Mark parties as loaded and check if all data is ready
                        if (!dataLoadedRef.current.parties) {
                            dataLoadedRef.current.parties = true;
                            console.log('🔄 [LOADING SCREEN] Dashboard: Parties data loaded, checking if all data is ready');
                            checkAndStopInitialLoading();
                        }
                    } catch (error) {
                        console.error('❌ Error updating parties:', error);
                        // Mark as loaded even on error to prevent infinite loading
                        if (!dataLoadedRef.current.parties) {
                            dataLoadedRef.current.parties = true;
                            checkAndStopInitialLoading();
                        }
                    }
                }, (error) => {
                    console.error('❌ Error setting up parties listener:', error);
                    // Mark as loaded even on error to prevent infinite loading
                    if (!dataLoadedRef.current.parties) {
                        dataLoadedRef.current.parties = true;
                        checkAndStopInitialLoading();
                    }
                });

                // Set up real-time listener for payments
                const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
                unsubscribePayments = onSnapshot(paymentsRef, (snapshot) => {
                    try {
                        console.log('🔄 [LOADING SCREEN] Dashboard: Payments snapshot received', {
                            hasData: !snapshot.empty,
                            count: snapshot.size,
                            isLoading
                        });
                        let paymentsData = [];
                        snapshot.forEach(doc => paymentsData.push({ id: doc.id, ...doc.data() }));
                        setPayments(paymentsData);
                        console.log('✅ Payments data updated in real-time');
                        
                        // Mark payments as loaded and check if all data is ready
                        if (!dataLoadedRef.current.payments) {
                            dataLoadedRef.current.payments = true;
                            console.log('🔄 [LOADING SCREEN] Dashboard: Payments data loaded, checking if all data is ready');
                            checkAndStopInitialLoading();
                        }
                    } catch (error) {
                        console.error('❌ Error updating payments:', error);
                        // Mark as loaded even on error to prevent infinite loading
                        if (!dataLoadedRef.current.payments) {
                            dataLoadedRef.current.payments = true;
                            checkAndStopInitialLoading();
                        }
                    }
                }, (error) => {
                    console.error('❌ Error setting up payments listener:', error);
                    // Mark as loaded even on error to prevent infinite loading
                    if (!dataLoadedRef.current.payments) {
                        dataLoadedRef.current.payments = true;
                        checkAndStopInitialLoading();
                    }
                });

                // Fetch sales + purchases (+ loans on native in same wave below)
                let salesBills = [];
                let purchaseBills = [];
                if (Capacitor.isNativePlatform()) {
                    const [salesResult, purchaseResult] = await Promise.all([
                        getDocs(getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills')).catch((error) => {
                            console.error('❌ Error loading sales bills:', error);
                            return null;
                        }),
                        getDocs(getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseBills')).catch((error) => {
                            console.error('❌ Error loading purchase bills:', error);
                            return null;
                        }),
                    ]);
                    if (salesResult) salesResult.forEach((docSnap) => salesBills.push({ id: docSnap.id, ...docSnap.data() }));
                    if (purchaseResult) purchaseResult.forEach((docSnap) => purchaseBills.push({ id: docSnap.id, ...docSnap.data() }));
                } else {
                    try {
                        const salesSnap = await getDocs(getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills'));
                        salesSnap.forEach(doc => salesBills.push({ id: doc.id, ...doc.data() }));
                    } catch (error) {
                        console.error('❌ Error loading sales bills:', error);
                        salesBills = [];
                    }
                    try {
                        const purchaseSnap = await getDocs(getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseBills'));
                        purchaseSnap.forEach(doc => purchaseBills.push({ id: doc.id, ...doc.data() }));
                    } catch (error) {
                        console.error('❌ Error loading purchase bills:', error);
                        purchaseBills = [];
                    }
                }

                // Calculate summary cards
                const today = new Date();
                const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                let monthlySales = 0;
                let monthlyPurchases = 0;
                let outstandingReceivables = 0;
                let outstandingPayables = 0;
                

                salesBills.forEach((bill, index) => {
                    try {

                        
                        const billDate = new Date(bill.invoiceDate || bill.date);
                        const totalAmount = parseFloat(bill.amount || bill.totalAmount || 0);
                        
                        // Monthly sales
                        if (billDate >= firstDayOfMonth && billDate <= today) {
                            monthlySales += totalAmount;
                        }
                        
                        // Outstanding receivables
                        const billPayments = paymentsData.filter(p =>
                            p.allocations && p.allocations.some(a => a.billId === bill.id && a.billType === 'invoice')
                        );
                        const totalPaid = billPayments.reduce((sum, payment) => {
                            const allocation = payment.allocations.find(a => a.billId === bill.id && a.billType === 'invoice');
                            return sum + (allocation ? allocation.allocatedAmount : 0);
                        }, 0);
                        
                        if (totalAmount > totalPaid) {
                            outstandingReceivables += (totalAmount - totalPaid);
                        }
                    } catch (error) {
                        console.error('❌ Error processing sales bill:', bill.id, error);
                    }
                });
                

                purchaseBills.forEach((bill, index) => {
                    try {

                        
                        const billDate = new Date(bill.billDate || bill.date);
                        const totalAmount = parseFloat(bill.amount || bill.totalAmount || 0);
                        
                        // Monthly purchases
                        if (billDate >= firstDayOfMonth && billDate <= today) {
                            monthlyPurchases += totalAmount;
                        }
                        
                        // Outstanding payables
                        const billPayments = paymentsData.filter(p =>
                            p.allocations && p.allocations.some(a => a.billId === bill.id && a.billType === 'purchase')
                        );
                        const totalPaid = billPayments.reduce((sum, payment) => {
                            const allocation = payment.allocations.find(a => a.billId === bill.id && a.billType === 'purchase');
                            return sum + (allocation ? allocation.allocatedAmount : 0);
                        }, 0);
                        
                        if (totalAmount > totalPaid) {
                            outstandingPayables += (totalAmount - totalPaid);
                        }
                    } catch (error) {
                        console.error('❌ Error processing purchase bill:', bill.id, error);
                    }
                });
                
                // Update state with calculated values
                setTotalSalesThisMonth(monthlySales);
                setTotalPurchasesThisMonth(monthlyPurchases);

                // Outstanding details will be calculated when parties data is loaded
                
                // Fetch loans and calculate total outstanding (including interest)
                try {
                    const loansRef = getCollectionForCompany(db, { companyId, appId, userId }, 'loans');
                    const loanPaymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'loanPayments');
                    let loansSnapshot;
                    let loanPaymentsSnapshot;
                    if (Capacitor.isNativePlatform()) {
                        [loansSnapshot, loanPaymentsSnapshot] = await Promise.all([
                            getDocs(loansRef),
                            getDocs(loanPaymentsRef),
                        ]);
                    } else {
                        loansSnapshot = await getDocs(loansRef);
                        loanPaymentsSnapshot = await getDocs(loanPaymentsRef);
                    }
                    const loans = [];
                    loansSnapshot.forEach(doc => loans.push({ id: doc.id, ...doc.data() }));
                    
                    const loanPaymentsByLoan = {};
                    loanPaymentsSnapshot.forEach(doc => {
                        const payment = { id: doc.id, ...doc.data() };
                        const loanId = payment.loanId;
                        if (!loanPaymentsByLoan[loanId]) {
                            loanPaymentsByLoan[loanId] = [];
                        }
                        loanPaymentsByLoan[loanId].push(payment);
                    });
                    
                    // Calculate total loans outstanding (including interest)
                    let totalLoansOutstandingAmount = 0;
                    loans.forEach(loan => {
                        const payments = loanPaymentsByLoan[loan.id] || [];
                        const totalPaid = payments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
                        const interestAccrued = calculateLoanInterest(
                            loan.principal || 0,
                            loan.interestRate || 0,
                            loan.loanDate,
                            payments,
                            loan.interestFrequency || 'yearly' // Use loan's interest frequency preference
                        );
                        const principalOutstanding = Math.max(0, (loan.principal || 0) - totalPaid);
                        const outstandingWithInterest = principalOutstanding + interestAccrued;
                        totalLoansOutstandingAmount += outstandingWithInterest;
                    });
                    
                    setTotalLoansOutstanding(totalLoansOutstandingAmount);
                    console.log(`✅ Loans outstanding calculated: ${totalLoansOutstandingAmount}`);
                } catch (error) {
                    console.error('❌ Error fetching loans:', error);
                    setTotalLoansOutstanding(0);
                }

                // Set up real-time listener for items
                const itemsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'items');
                unsubscribeItems = onSnapshot(itemsRef, (snapshot) => {
                    try {
                        setTotalItems(snapshot.size);
                        console.log('✅ Items data updated in real-time');
                        
                        // Mark items as loaded and check if all data is ready
                        if (!dataLoadedRef.current.items) {
                            dataLoadedRef.current.items = true;
                            console.log('🔄 [LOADING SCREEN] Dashboard: Items data loaded, checking if all data is ready');
                            checkAndStopInitialLoading();
                        }
                    } catch (error) {
                        console.error('❌ Error updating items:', error);
                        // Mark as loaded even on error to prevent infinite loading
                        if (!dataLoadedRef.current.items) {
                            dataLoadedRef.current.items = true;
                            checkAndStopInitialLoading();
                        }
                    }
                }, (error) => {
                    console.error('❌ Error setting up items listener:', error);
                    // Mark as loaded even on error to prevent infinite loading
                    if (!dataLoadedRef.current.items) {
                        dataLoadedRef.current.items = true;
                        checkAndStopInitialLoading();
                    }
                });

                // Normalize full company doc (name, basicInfo, branding, legacy details.*) — do not use
                // setCompanyDetails(d.details) alone; it drops root name/branding and uses wrong field names.
                try {
                    if (companyId) {
                        const cRes = await fetchCompanyDetails(db, { companyId, appId, userId });
                        if (cRes?.success && cRes?.data) {
                            setCompanyDetails(prev => ({ ...prev, ...cRes.data }));
                        }
                    }
                } catch (error) {
                    console.error('❌ Error loading company details:', error);
                }
                console.log('🔄 [LOADING SCREEN] Dashboard: fetchDashboardData completed - checking if loading should stop');
                // Don't stop loading here - let the onSnapshot callbacks handle it
                // This ensures we wait for actual Firebase data, not just async operations
                setMessage('Dashboard ready!');

            } catch (error) {
                console.error('❌ Error in fetchDashboardData:', error);
                setHasError(true);
                setErrorMessage(error.message || 'Failed to load dashboard data');
                // Set default values on error
                setPartiesList([]);
                setPayments([]);
                setUserInfo(null);
                setCompanyInfo(null);
                setCompanyDetails({ firmName: '', gstin: '', contactNumber: '', address: '', gstinType: '' });
                setTotalCustomers(0);
                setTotalSuppliers(0);
                setTotalItems(0);
                setTotalSalesThisMonth(0);
                setTotalPurchasesThisMonth(0);
                setTotalLoansOutstanding(0);
                setIsLoading(false);
                stopLoading();
                setMessage('Dashboard data loaded successfully!');
            }
        };


            
            fetchDashboardData();
            
            // Cleanup function to handle user sign out
            return () => {
                // Clear all state when component unmounts or user changes
                setPartiesList([]);
                setPayments([]);
                setUserInfo(null);
                setCompanyInfo(null);
                setCompanyDetails({ firmName: '', gstin: '', contactNumber: '', address: '', gstinType: '' });
                setTotalCustomers(0);
                setTotalSuppliers(0);
                setTotalItems(0);
                setTotalSalesThisMonth(0);
                setTotalPurchasesThisMonth(0);
                setTotalLoansOutstanding(0);
                setPartyOutstanding([]);
            };

            // Return cleanup function to unsubscribe from listeners
            return () => {
                if (unsubscribeParties) unsubscribeParties();
                if (unsubscribePayments) unsubscribePayments();
                if (unsubscribeItems) unsubscribeItems();
            };
        }, [db, userId, isAuthReady, appId, companyId]); // Keep dependencies but ensure fetchDashboardData is stable

    // Calculate party-wise outstanding/receivable
    useEffect(() => {
        console.log('🔍 Outstanding useEffect triggered:', {
            partiesCount: partiesList.length,
            paymentsCount: payments.length,
            hasDb: !!db,
            hasUserId: !!userId,
            isAuthReady,
            hasCompanyId: !!companyId
        });
        
        if (!partiesList.length || !payments.length || !db || !userId || !isAuthReady || !companyId) {
            console.log('⏭️ Skipping outstanding calculation - missing data');
            return;
        }
        
        // Fetch all salesBills and purchaseBills (native: parallel + deferred slightly after first paint)
        const fetchBills = async () => {
            try {
                if (Capacitor.isNativePlatform()) {
                    await new Promise((resolve) => {
                        if (typeof requestAnimationFrame === 'function') {
                            requestAnimationFrame(() => setTimeout(resolve, 50));
                        } else {
                            setTimeout(resolve, 50);
                        }
                    });
                }
                const salesRef = getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills');
                const purchaseRef = getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseBills');
                let salesBills = [];
                let purchaseBills = [];
                let outstandingMap = {};
                
                // 🔥 FIX: Use getDocs instead of onSnapshot to avoid Firestore state errors
                if (Capacitor.isNativePlatform()) {
                    const [salesSnapshot, purchaseSnapshot] = await Promise.all([
                        getDocs(salesRef).catch((error) => {
                            console.error('❌ Failed to fetch sales bills:', error);
                            return null;
                        }),
                        getDocs(purchaseRef).catch((error) => {
                            console.error('❌ Failed to fetch purchase bills:', error);
                            return null;
                        }),
                    ]);
                    salesBills = salesSnapshot ? salesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) : [];
                    purchaseBills = purchaseSnapshot ? purchaseSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) : [];
                } else {
                    try {
                        const salesSnapshot = await getDocs(salesRef);
                        salesBills = salesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    } catch (error) {
                        console.error('❌ Failed to fetch sales bills:', error);
                        salesBills = [];
                    }
                    try {
                        const purchaseSnapshot = await getDocs(purchaseRef);
                        purchaseBills = purchaseSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                    } catch (error) {
                        console.error('❌ Failed to fetch purchase bills:', error);
                        purchaseBills = [];
                    }
                }
                
                // 🔥 CALC OPTIMIZATION: Optimized O(n) outstanding calculation (was O(n³))
                console.time('⚡ Outstanding Calculation');
                
                // Step 1: Build payment allocation map O(payments)
                const paymentsByBill = new Map();
                payments.forEach(payment => {
                    if (payment.allocations && Array.isArray(payment.allocations)) {
                        payment.allocations.forEach(alloc => {
                            const key = `${alloc.billId}_${alloc.billType}`;
                            const current = paymentsByBill.get(key) || 0;
                            paymentsByBill.set(key, current + (parseFloat(alloc.allocatedAmount) || 0));
                        });
                    }
                });
                
                // Step 2: Build party bills maps O(bills)
                const salesByParty = new Map();
                const purchasesByParty = new Map();
                
                salesBills.forEach(bill => {
                    const partyId = bill.party || bill.customerId;
                    if (partyId) {
                        if (!salesByParty.has(partyId)) {
                            salesByParty.set(partyId, []);
                        }
                        salesByParty.get(partyId).push(bill);
                    }
                });
                
                purchaseBills.forEach(bill => {
                    const partyId = bill.party || bill.supplierId;
                    if (partyId) {
                        if (!purchasesByParty.has(partyId)) {
                            purchasesByParty.set(partyId, []);
                        }
                        purchasesByParty.get(partyId).push(bill);
                    }
                });
                
                // Step 3: Calculate outstanding for each party O(parties)
                // outstandingMap already declared above
                partiesList.forEach(party => {
                    let receivable = party.openingBalance > 0 ? party.openingBalance : 0;
                    let payable = party.openingBalance < 0 ? Math.abs(party.openingBalance) : 0;
                    
                    // Sales (receivable) - O(party's sales)
                    const partySales = salesByParty.get(party.id) || [];
                    partySales.forEach(bill => {
                        const totalAmount = parseFloat(bill.amount || bill.totalAmount || 0);
                        const paid = paymentsByBill.get(`${bill.id}_invoice`) || 0;
                        const outstanding = totalAmount - paid;
                        if (outstanding > 0) {
                            receivable += outstanding;
                        }
                    });
                    
                    // Purchases (payable) - O(party's purchases)
                    const partyPurchases = purchasesByParty.get(party.id) || [];
                    partyPurchases.forEach(bill => {
                        const totalAmount = parseFloat(bill.amount || bill.totalAmount || 0);
                        const paid = paymentsByBill.get(`${bill.id}_purchase`) || 0;
                        const outstanding = totalAmount - paid;
                        if (outstanding > 0) {
                            payable += outstanding;
                        }
                    });
                    
                    outstandingMap[party.id] = {
                        partyName: party.firmName,
                        receivable,
                        payable
                    };
                });
                
                setPartyOutstanding(Object.values(outstandingMap));
                console.timeEnd('⚡ Outstanding Calculation');
                console.log(`✅ CALC OPTIMIZATION: Processed ${partiesList.length} parties, ${salesBills.length} sales, ${purchaseBills.length} purchases, ${payments.length} payments`);
            
            
            } catch (error) {
                console.error('❌ Error in fetchBills:', error);
                setPartyOutstanding([]);
            }
        };
        
        fetchBills();
            
            // Cleanup function
            return () => {
                // Clear outstanding data when component unmounts or dependencies change
                setPartyOutstanding([]);
            };
        }, [partiesList, payments, db, userId, isAuthReady, appId, companyId]);


    const toggleTodo = async (id) => {
        const updatedTodos = todos.map(todo => 
            todo.id === id ? { ...todo, completed: !todo.completed } : todo
        );
        setTodos(updatedTodos);
        
        // Save to Firebase
        if (db && userId && isAuthReady && auth.currentUser) {
            try {
                const todoRef = doc(getCollectionForCompany(db, { companyId, appId, userId }, 'todolist'), id.toString());
                const todo = updatedTodos.find(t => t.id === id);
                await setDoc(todoRef, todo);
            } catch (error) {
                console.error('❌ Error saving todo:', error);
            }
        }
    };

    const addTodo = async () => {
        if (!newTodoText.trim()) return;
        
        const newTodo = {
            id: Date.now(),
            text: newTodoText.trim(),
            completed: false,
            priority: newTodoPriority,
            category: newTodoCategory,
            createdAt: new Date().toISOString()
        };
        
        const updatedTodos = [...todos, newTodo];
        setTodos(updatedTodos);
        
        // Save to Firebase
        if (db && userId && isAuthReady && auth.currentUser) {
            try {
                const todoRef = doc(getCollectionForCompany(db, { companyId, appId, userId }, 'todolist'), newTodo.id.toString());
                await setDoc(todoRef, newTodo);
            } catch (error) {
                console.error('❌ Error saving new todo:', error);
            }
        }
        
        setNewTodoText('');
        setNewTodoPriority('medium');
        setNewTodoCategory('personal');
        setShowAddTodo(false);
    };

    const removeTodo = async (id) => {
        const updatedTodos = todos.filter(todo => todo.id !== id);
        setTodos(updatedTodos);
        
        // Remove from Firebase
        if (db && userId && isAuthReady && auth.currentUser) {
            try {
                const todoRef = doc(getCollectionForCompany(db, { companyId, appId, userId }, 'todolist'), id.toString());
                await deleteDoc(todoRef);
            } catch (error) {
                console.error('❌ Error removing todo:', error);
            }
        }
    };

    const getCategoryColor = (category) => {
        switch(category) {
            case 'personal': return 'bg-blue-100 text-blue-800';
            case 'meeting': return 'bg-green-100 text-green-800';
            case 'urgent': return 'bg-red-100 text-red-800';
            case 'followup': return 'bg-yellow-100 text-yellow-800';
            case 'business': return 'bg-purple-100 text-purple-800';
            case 'reminder': return 'bg-orange-100 text-orange-800';
            case 'project': return 'bg-indigo-100 text-indigo-800';
            case 'other': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getCategoryLabel = (category) => {
        switch(category) {
            case 'personal': return 'Personal';
            case 'meeting': return 'Meeting';
            case 'urgent': return 'Urgent';
            case 'followup': return 'Follow Up';
            case 'business': return 'Business';
            case 'reminder': return 'Reminder';
            case 'project': return 'Project';
            case 'other': return 'Other';
            default: return 'Other';
        }
    };

    const filteredTodos = filterCategory === 'all' 
        ? todos 
        : todos.filter(todo => todo.category === filterCategory);

    const pendingTodos = todos.filter(todo => !todo.completed);

    const getPriorityColor = (priority) => {
        switch(priority) {
            case 'high': return 'text-red-600 bg-red-100';
            case 'medium': return 'text-yellow-600 bg-yellow-100';
            case 'low': return 'text-green-600 bg-green-100';
            default: return 'text-gray-600 bg-gray-100';
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: companyCurrency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Derived company display fields for the dashboard footer
    const companyNameDisplay = (
      companyDetails?.firmName ||
      companyDetails?.name ||
      companyInfo?.companyName ||
      'Company Name'
    );
    const companyInitials = (
      companyDetails?.firmName ||
      companyDetails?.name ||
      companyInfo?.companyName ||
      'AC'
    ).substring(0, 2).toUpperCase();
    const companyLogoUrl = companyDetails?.logoUrl || companyInfo?.logoUrl || '';
    const gstTypeDisplay = (
      companyDetails?.gstinType ||
      getCompanyTypeLabel?.(companyInfo?.companyType || companyDetails?.companyType, companyInfo?.companyTypeOther) ||
      'GST Type'
    );
    const gstinDisplay = (companyDetails?.gstin || companyDetails?.gstNumber || companyInfo?.gstNumber || 'GST Number');
    const contactDisplay = (companyDetails?.contactNumber || companyDetails?.phone || companyInfo?.phone || 'N/A');
    const emailDisplay = (companyDetails?.email || companyInfo?.email || 'N/A');
    const addressDisplay = ([companyDetails?.address, companyDetails?.city, companyDetails?.state, companyDetails?.pincode]
      .filter(Boolean)
      .join(', ') || [companyInfo?.address, companyInfo?.city, companyInfo?.state, companyInfo?.pincode]
      .filter(Boolean)
      .join(', ') || 'N/A');

    
    // Show/Hide toggle for business overview

    // Show loading screen while initial data is being fetched
    // Show if we're in initial loading state (set immediately on mount, before any data operations)
    const shouldShowLoading = isInitialLoading && db && userId && isAuthReady;
    
    if (shouldShowLoading) {
        if (!hasShownLoading) {
            setHasShownLoading(true);
        }
        console.log('🔄 [LOADING SCREEN] Dashboard: RENDERING LOADING SCREEN NOW', {
            isInitialLoading,
            isLoading,
            partiesCount: partiesList.length,
            paymentsCount: payments.length,
            hasDb: !!db,
            hasUserId: !!userId,
            isAuthReady,
            hasCompanyId: !!companyId,
            dataLoaded: dataLoadedRef.current,
            loadingStartTime,
            elapsed: loadingStartTime ? Date.now() - loadingStartTime : 0
        });
        return (
            <LoadingScreen 
                message="Loading Dashboard..."
                subMessage="Fetching your business data from Firebase"
                fullScreen={true}
            />
        );
    }
    
    // Log when loading screen should NOT be shown
    if (!shouldShowLoading) {
        console.log('✅ [LOADING SCREEN] Dashboard: NOT showing loading screen', {
            isInitialLoading,
            isLoading,
            partiesCount: partiesList.length,
            paymentsCount: payments.length,
            dataLoaded: dataLoadedRef.current,
            reason: isInitialLoading ? 'missing dependencies' : 'not loading'
        });
    }

    return (
        <React.Fragment>
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 relative">
            {/* Floating Todo Button - Mobile/Android Only - Always Visible */}
            {isMobileView && (
              <TodoFloatingButton
                todos={todos}
                onToggleTodo={toggleTodo}
                onAddTodo={async (text, priority, category) => {
                  if (!text.trim()) return;
                  
                  const newTodo = {
                      id: Date.now(),
                      text: text.trim(),
                      completed: false,
                      priority: priority,
                      category: category,
                      createdAt: new Date().toISOString()
                  };
                  
                  const updatedTodos = [...todos, newTodo];
                  setTodos(updatedTodos);
                  
                  // Save to Firebase
                  if (db && userId && isAuthReady && auth.currentUser) {
                      try {
                          const todoRef = doc(getCollectionForCompany(db, { companyId, appId, userId }, 'todolist'), newTodo.id.toString());
                          await setDoc(todoRef, newTodo);
                      } catch (error) {
                          console.error('❌ Error saving new todo:', error);
                      }
                  }
                }}
                onRemoveTodo={removeTodo}
                getPriorityColor={getPriorityColor}
                getCategoryLabel={getCategoryLabel}
              />
            )}
            
            <div id="dashboard-navbar" className="p-3 sm:p-6 lg:p-8 xl:p-12 w-full max-w-full overflow-x-hidden">
                {/* TOP NAVIGATION / DASHBOARD HEADER */}
                <div className="mb-6 sm:mb-8 lg:mb-12" data-section="dashboard-header">
                    <div className="flex flex-wrap items-center justify-between gap-3 sm:gap-4 mb-4">
                      <div className="flex items-center gap-4">
                        <div className="p-3 lg:p-4 bg-gradient-to-r from-blue-100 to-purple-100 rounded-2xl">
                          <svg className="w-8 h-8 lg:w-10 lg:h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2 2z" />
                          </svg>
                        </div>
                        <div>
                          <h1 id="dashboard-title" className="text-xl lg:text-2xl font-semibold text-gray-800 mb-2">{t('dashboard')}</h1>
                          <p className="text-sm lg:text-base text-gray-600">{t('welcomeBack')}</p>
                        </div>
                      </div>
                      
                      {/* Quick Access Todo Button - Mobile/Android Only */}
                      {isMobileView && (
                        <button
                          onClick={() => {
                            // Trigger modal open via TodoFloatingButton
                            const fabButton = document.getElementById('todo-fab-button');
                            if (fabButton) fabButton.click();
                          }}
                          className="lg:hidden relative flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 active:scale-95"
                        >
                          {pendingTodos.length > 0 && (
                            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-lg border-2 border-white">
                              {pendingTodos.length > 9 ? '9+' : pendingTodos.length}
                            </span>
                          )}
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                          </svg>
                          <span className="text-white font-semibold text-sm">
                            Tasks
                          </span>
                        </button>
                      )}
                    </div>
                </div>

                {/* Main Dashboard Layout - Full Screen */}
                <div className="w-full">
                    {/* Loading and Error States */}
                    {isLoading && (
                        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center gap-3">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                <span className="text-blue-800 font-medium">{t('loadingDashboard')}</span>
                            </div>
                        </div>
                    )}
                    
                    {hasError && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-center gap-3">
                                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                                <div>
                                    <span className="text-red-800 font-medium">{t('errorLoadingDashboard')}</span>
                                    <p className="text-red-600 text-sm mt-1">{errorMessage}</p>
                                </div>
                                <button 
                                    onClick={() => window.location.reload()} 
                                    className="ml-auto px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                                >
                                    {t('retry')}
                                </button>
                            </div>
                        </div>
                    )}
                    
                    {/* Quick Action + Todo List (Side by Side) - New Layout */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8 mb-8 lg:mb-12">
                        {/* Left Side: Quick Actions */}
                        <div className="xl:col-span-1" id="dashboard-quick-actions" data-section="quick-actions">
                            <div className="bg-white rounded-2xl shadow-xl p-6 lg:p-8 border border-gray-100 quick-actions-section h-full">
                                <div className="flex items-center gap-3 mb-6 lg:mb-8">
                                    <div className="p-3 lg:p-4 bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl">
                                        <svg className="w-6 h-6 lg:w-8 lg:h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="text-lg lg:text-xl font-semibold text-gray-800">{t('quickActions')}</h2>
                                        <p className="text-sm lg:text-base text-gray-600">{t('quickActionsDesc')}</p>
                                    </div>
                                </div>
                                
                                {/* Quick Actions - Responsive Grid (min 2 per row on mobile) */}
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
                                    {/* Row 1 */}
                                    <Link to="/sales" className="p-4 bg-green-100 hover:bg-green-200 rounded-xl text-center transition-all duration-200 hover:scale-105">
                                        <div className="p-3 bg-green-200 rounded-lg mx-auto mb-2 w-12 h-12 flex items-center justify-center">
                                            <svg className="w-6 h-6 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                            </svg>
                                        </div>
                                        <span className="text-xs sm:text-sm font-medium text-green-800 truncate block">{t('sales')}</span>
                                    </Link>
                                    
                                    <Link to="/purchases" className="p-4 bg-blue-100 hover:bg-blue-200 rounded-xl text-center transition-all duration-200 hover:scale-105">
                                        <div className="p-3 bg-blue-200 rounded-lg mx-auto mb-2 w-12 h-12 flex items-center justify-center">
                                            <svg className="w-6 h-6 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                            </svg>
                                        </div>
                                        <span className="text-xs sm:text-sm font-medium text-blue-800 truncate block">{t('purchase')}</span>
                                    </Link>
                                    
                                    <Link to="/payments" className="p-4 bg-purple-100 hover:bg-purple-200 rounded-xl text-center transition-all duration-200 hover:scale-105">
                                        <div className="p-3 bg-purple-200 rounded-lg mx-auto mb-2 w-12 h-12 flex items-center justify-center">
                                            <svg className="w-6 h-6 text-purple-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                        </div>
                                        <span className="text-xs sm:text-sm font-medium text-purple-800 truncate block">{t('payment')}</span>
                                    </Link>
                                    
                                    {/* Row 2 */}
                                    <Link to="/parties" className="p-4 bg-orange-100 hover:bg-orange-200 rounded-xl text-center transition-all duration-200 hover:scale-105">
                                        <div className="p-3 bg-orange-200 rounded-lg mx-auto mb-2 w-12 h-12 flex items-center justify-center">
                                            <svg className="w-6 h-6 text-orange-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                        </div>
                                        <span className="text-xs sm:text-sm font-medium text-orange-800 truncate block">{t('parties')}</span>
                                    </Link>
                                    
                                    <Link to="/items" className="p-3 sm:p-4 bg-blue-100 hover:bg-blue-200 rounded-xl text-center transition-all duration-200 hover:scale-105 touch-manipulation">
                                        <div className="p-3 bg-blue-200 rounded-lg mx-auto mb-2 w-12 h-12 flex items-center justify-center">
                                            <svg className="w-6 h-6 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                            </svg>
                                        </div>
                                        <span className="text-xs sm:text-sm font-medium text-blue-800 truncate block">{t('items')}</span>
                                    </Link>
                                    
                                    <Link to="/expenses" className="p-4 bg-red-100 hover:bg-red-200 rounded-xl text-center transition-all duration-200 hover:scale-105">
                                        <div className="p-3 bg-red-200 rounded-lg mx-auto mb-2 w-12 h-12 flex items-center justify-center">
                                            <svg className="w-6 h-6 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2m7-5H7" />
                                            </svg>
                                        </div>
                                        <span className="text-xs sm:text-sm font-medium text-red-800 truncate block">{t('expenses')}</span>
                                    </Link>
                                    
                                    {/* Row 3 */}
                                    <Link to="/reports" className="p-4 bg-teal-100 hover:bg-teal-200 rounded-xl text-center transition-all duration-200 hover:scale-105">
                                        <div className="p-3 bg-teal-200 rounded-lg mx-auto mb-2 w-12 h-12 flex items-center justify-center">
                                            <svg className="w-6 h-6 text-teal-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                            </svg>
                                        </div>
                                        <span className="text-xs sm:text-sm font-medium text-teal-800 truncate block">{t('reports')}</span>
                                    </Link>

                                    <Link to="/business-analytics" className="p-4 bg-indigo-100 hover:bg-indigo-200 rounded-xl text-center transition-all duration-200 hover:scale-105">
                                        <div className="p-3 bg-indigo-200 rounded-lg mx-auto mb-2 w-12 h-12 flex items-center justify-center">
                                            <svg className="w-6 h-6 text-indigo-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3h2v18h-2zM4 13h2v8H4zm14-6h2v14h-2zM7.5 9.5l3 2.5 5-5" />
                                            </svg>
                                        </div>
                                        <span className="text-xs sm:text-sm font-medium text-indigo-800 truncate block">Analytics</span>
                                    </Link>
                                    
                                    <Link to="/taxes" className="p-4 bg-yellow-100 hover:bg-yellow-200 rounded-xl text-center transition-all duration-200 hover:scale-105">
                                        <div className="p-3 bg-yellow-200 rounded-lg mx-auto mb-2 w-12 h-12 flex items-center justify-center">
                                            <svg className="w-6 h-6 text-yellow-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6M9 16h6M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z" />
                                            </svg>
                                        </div>
                                        <span className="text-xs sm:text-sm font-medium text-yellow-800 truncate block">{t('taxes')}</span>
                                    </Link>
                                    
                                    <Link to="/settings" className="p-4 bg-gray-100 hover:bg-gray-200 rounded-xl text-center transition-all duration-200 hover:scale-105">
                                        <div className="p-3 bg-gray-200 rounded-lg mx-auto mb-2 w-12 h-12 flex items-center justify-center">
                                            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        </div>
                                        <span className="text-xs sm:text-sm font-medium text-gray-800 truncate block">{t('settings')}</span>
                                    </Link>

                                    <Link to="/import-data" className="p-4 bg-indigo-100 hover:bg-indigo-200 rounded-xl text-center transition-all duration-200 hover:scale-105">
                                        <div className="p-3 bg-indigo-200 rounded-lg mx-auto mb-2 w-12 h-12 flex items-center justify-center">
                                            <svg className="w-6 h-6 text-indigo-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                                            </svg>
                                        </div>
                                        <span className="text-xs sm:text-sm font-medium text-indigo-800 truncate block">Import Data</span>
                                        <p className="text-xs text-indigo-600">Marg, Tally, Vyapar, Zoho</p>
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Right Side: Todo List */}
                        <div className="xl:col-span-1" id="dashboard-todo" data-section="tasks">
                            <div className="bg-white rounded-2xl shadow-xl p-6 lg:p-8 border border-gray-100 h-full">
                                <div className="flex items-center gap-3 mb-6 lg:mb-8">
                                    <div className="p-2 lg:p-3 bg-green-100 rounded-xl">
                                        <svg className="w-5 h-5 lg:w-6 lg:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg lg:text-xl font-semibold text-gray-800">To-Do List</h3>
                                        <p className="text-sm lg:text-base text-gray-600">Manage your daily tasks and priorities</p>
                                    </div>
                                </div>

                                {/* Add Todo Input */}
                                <div className="mb-6">
                                    <div className="space-y-4">
                                        <div className="flex flex-col sm:flex-row gap-3">
                                            <input
                                                type="text"
                                                value={newTodoText}
                                                onChange={(e) => setNewTodoText(e.target.value)}
                                                placeholder="+ Add New Task"
                                                className="flex-1 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm lg:text-base"
                                                onKeyPress={(e) => e.key === 'Enter' && addTodo()}
                                            />
                                            <button
                                                onClick={addTodo}
                                                className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors duration-200 font-medium text-sm sm:text-base whitespace-nowrap"
                                            >
                                                Add
                                            </button>
                                        </div>
                                        
                                        {/* Category and Priority Selection */}
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                                                <AppSelect
                                                    value={newTodoCategory}
                                                    onChange={(e) => setNewTodoCategory(e.target.value)}
                                                    aria-label="Category"
                                                    sheetTitle="Category"
                                                    options={DASHBOARD_TODO_CATEGORIES_EMOJI}
                                                    triggerClassName={dashboardTodoSelectTrigger}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Priority</label>
                                                <AppSelect
                                                    value={newTodoPriority}
                                                    onChange={(e) => setNewTodoPriority(e.target.value)}
                                                    aria-label="Priority"
                                                    sheetTitle="Priority"
                                                    options={DASHBOARD_TODO_PRIORITIES_EMOJI}
                                                    triggerClassName={dashboardTodoSelectTrigger}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Todo List */}
                                <div className="space-y-3 max-h-80 overflow-y-auto">
                                    {filteredTodos.length > 0 ? (
                                        filteredTodos.map((todo) => (
                                            <div key={todo.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                                                <button
                                                    onClick={() => toggleTodo(todo.id)}
                                                    className={`flex-shrink-0 w-5 h-5 rounded-full border-2 transition-colors duration-200 ${
                                                        todo.completed 
                                                            ? 'bg-green-500 border-green-500 text-white' 
                                                            : 'border-gray-300 hover:border-green-400'
                                                    }`}
                                                >
                                                    {todo.completed && (
                                                        <svg className="w-3 h-3 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    )}
                                                </button>
                                                <div className="flex-1 min-w-0">
                                                    <p className={`text-sm font-medium ${todo.completed ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                                                        {todo.text}
                                                    </p>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(todo.category)}`}>
                                                            {getCategoryLabel(todo.category)}
                                                        </span>
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(todo.priority)}`}>
                                                            {todo.priority}
                                                        </span>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => removeTodo(todo.id)}
                                                    className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors duration-200"
                                                >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                    </svg>
                                                </button>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-8 text-gray-500">
                                            <div className="mb-4">
                                                <svg className="w-16 h-16 mx-auto text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                                </svg>
                                            </div>
                                            <h4 className="text-lg font-semibold text-gray-600 mb-2">Welcome to Your Todo List!</h4>
                                            <p className="text-sm text-gray-500 mb-4">Start organizing your tasks and stay productive</p>
                                            <button
                                                onClick={() => setShowAddTodo(true)}
                                                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 font-medium"
                                            >
                                                Create Your First Task
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Category Filter - Always show for better UX */}
                                <div className="mt-6 pt-4 border-t border-gray-200">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                                            </svg>
                                        </div>
                                        <label className="text-sm font-semibold text-gray-700">Filter by Category</label>
                                    </div>
                                    <AppSelect
                                        value={filterCategory}
                                        onChange={(e) => setFilterCategory(e.target.value)}
                                        aria-label="Filter by category"
                                        sheetTitle="Filter by category"
                                        options={DASHBOARD_TODO_FILTER_CATEGORIES}
                                        triggerClassName={dashboardFilterSelectTrigger}
                                    />
                                    {todos.length > 0 && (
                                        <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                                            <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                                            Showing {filteredTodos.length} of {todos.length} tasks
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* COMPANY INFO - Compact Footer Section */}
                    <div className="mt-8 lg:mt-12" id="dashboard-company-info" data-section="company-info">
                        <div className="bg-white rounded-2xl shadow-xl p-4 lg:p-6 border border-gray-100">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl">
                                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-800">Company Information</h3>
                            </div>
                            
                            {/* 4-Column Compact Layout */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                                {/* Column 1: LOGO + Company Name + GST Type */}
                                <div className="flex flex-col items-center text-center">
                                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-3 overflow-hidden shrink-0">
                                        {companyLogoUrl ? (
                                            <img
                                                src={companyLogoUrl}
                                                alt=""
                                                className="w-full h-full object-contain bg-white"
                                            />
                                        ) : (
                                            <span className="text-white text-xl font-bold">{companyInitials}</span>
                                        )}
                                    </div>
                                    <h4 className="font-semibold text-gray-800 mb-1">{companyNameDisplay}</h4>
                                    <p className="text-sm text-gray-600">{gstTypeDisplay}</p>
                                </div>
                                
                                {/* Column 2: GST Number + Contact + Email */}
                                <div className="space-y-2 min-w-0">
                                    <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
                                        <span className="text-sm font-medium text-gray-700 shrink-0">GSTIN:</span>
                                        <span className="text-sm text-gray-900 break-all">{gstinDisplay}</span>
                                    </div>
                                    <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
                                        <span className="text-sm font-medium text-gray-700 shrink-0">Contact:</span>
                                        <span className="text-sm text-gray-900 break-all">{contactDisplay}</span>
                                    </div>
                                    <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
                                        <span className="text-sm font-medium text-gray-700 shrink-0">Email:</span>
                                        <span className="text-sm text-gray-900 break-all">{emailDisplay}</span>
                                    </div>
                                </div>
                                
                                {/* Column 3: Customer count + Supplier count + Items count + Loans Outstanding */}
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-700">{t('customers')}</span>
                                        <span className="text-sm font-semibold text-blue-600">{totalCustomers}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-700">{t('suppliers')}</span>
                                        <span className="text-sm font-semibold text-green-600">{totalSuppliers}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-gray-700">{t('items')}:</span>
                                        <span className="text-sm font-semibold text-purple-600">{totalItems}</span>
                                    </div>
                                    {totalLoansOutstanding > 0 && (
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-gray-700">Loans Outstanding:</span>
                                            <span className="text-sm font-semibold text-red-600">{formatCurrency(totalLoansOutstanding)}</span>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Column 4: Address */}
                                <div className="space-y-2 min-w-0">
                                    <span className="text-sm font-medium text-gray-700">{t('address')}</span>
                                    <p className="text-sm text-gray-900 leading-relaxed break-words">{addressDisplay}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Software Footer - Name, Tagline & Help Contact */}
                    <div className="mt-8 lg:mt-12">
                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl shadow-xl p-6 lg:p-8 border border-blue-100">
                            <div className="text-center">
                                <div className="mb-4">
                                    <h3 className="text-2xl lg:text-3xl font-bold text-blue-800 mb-2">ACCTOO</h3>
                                    <p className="text-lg lg:text-xl text-blue-600 font-medium">"{t('simplifyingAccounting')}"</p>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                                    <div className="text-center">
                                        <Link to="/help-support" className="block group hover:scale-105 transition-transform duration-200">
                                            <div className="p-3 bg-blue-100 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                                                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <h4 className="text-lg font-semibold text-gray-800 mb-2 group-hover:text-blue-700 transition-colors">Need Help?</h4>
                                            <p className="text-sm text-gray-600 group-hover:text-gray-700 transition-colors">Click here for help & support</p>
                                        </Link>
                                    </div>
                                    
                                    <div className="text-center">
                                        <div className="p-3 bg-green-100 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                                            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                            </svg>
                                        </div>
                                        <h4 className="text-lg font-semibold text-gray-800 mb-2">Contact Support</h4>
                                        <p className="text-sm text-gray-600">Call us anytime for assistance</p>
                                        <p className="text-lg font-bold text-green-600 mt-2">+91 7000637393</p>
                                    </div>
                                    
                                    <div className="text-center">
                                        <div className="p-3 bg-purple-100 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                                            <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        <h4 className="text-lg font-semibold text-gray-800 mb-2">Email Support</h4>
                                        <p className="text-sm text-gray-600">Send us an email for detailed help</p>
                                        <p className="text-lg font-bold text-purple-600 mt-2">support@acctoo.com</p>
                                    </div>
                                </div>
                                
                                <div className="mt-6 pt-6 border-t border-blue-200">
                                    <div className="text-center mb-4">
                                        <div className="inline-flex items-center gap-2 text-sm text-blue-600 mb-2">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                            <span className="font-medium">{t('address')}</span>
                                            <span>{addressDisplay}</span>
                                        </div>
                                    </div>
                                    <p className="text-sm text-blue-600">
                                        © 2024 ACCTOO. {t('allRightsReserved')} | 
                                        <span className="font-medium"> "{t('simplifyingAccounting')}"</span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            

            {/* Add Todo Modal */}
            {showAddTodo && (
                <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                    <div className="bg-white rounded-2xl p-6 lg:p-8 max-w-md w-full mx-4">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-semibold text-gray-800">Add New Task</h3>
                            <button
                                onClick={() => setShowAddTodo(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Task Description</label>
                                <input
                                    type="text"
                                    value={newTodoText}
                                    onChange={(e) => setNewTodoText(e.target.value)}
                                    placeholder="What do you want to get done?"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    onKeyPress={(e) => e.key === 'Enter' && addTodo()}
                                />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                                    <AppSelect
                                        value={newTodoPriority}
                                        onChange={(e) => setNewTodoPriority(e.target.value)}
                                        aria-label="Priority"
                                        sheetTitle="Priority"
                                        options={DASHBOARD_TODO_PRIORITIES_PLAIN}
                                        triggerClassName={dashboardTodoSelectTriggerLg}
                                    />
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                                    <AppSelect
                                        value={newTodoCategory}
                                        onChange={(e) => setNewTodoCategory(e.target.value)}
                                        aria-label="Category"
                                        sheetTitle="Category"
                                        options={DASHBOARD_TODO_CATEGORIES_PLAIN}
                                        triggerClassName={dashboardTodoSelectTriggerLg}
                                    />
                                </div>
                            </div>
                            
                            <div className="flex gap-3 pt-4">
                                <button
                                    onClick={() => setShowAddTodo(false)}
                                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={addTodo}
                                    className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Add Task
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
        </React.Fragment>
    );
};

export default Dashboard; 